import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { soccerPath, soccerUrl } from '../utils/paths';
import './ClubTournament.css';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const statusBadge = (status) => {
  const map = {
    DRAFT: { label: 'Draft', className: 'tournament-badge--draft' },
    LIVE: { label: 'Live', className: 'tournament-badge--live' },
    FINAL: { label: 'Final', className: 'tournament-badge--final' },
  };
  return map[status] || { label: status, className: '' };
};

const ClubTournamentAdmin = () => {
  const { id: clubId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [club, setClub] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', startDate: '', endDate: '' });

  const loadData = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      setError('');
      const [clubData, tournamentsData] = await Promise.all([
        api.getClubById(clubId),
        api.getClubTournaments(clubId, user.id),
      ]);
      setClub(clubData);
      setTournaments(Array.isArray(tournamentsData) ? tournamentsData : []);
    } catch (err) {
      setError(
        api.isTournamentApiUnavailable(err)
          ? 'Tournament service is not available yet. The backend may still be deploying — please try again in a few minutes.'
          : (err.message || 'Failed to load tournaments')
      );
    } finally {
      setLoading(false);
    }
  }, [clubId, user?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Tournament name is required');
      return;
    }
    setCreating(true);
    setError('');
    try {
      const created = await api.createClubTournament(clubId, user.id, {
        name: form.name.trim(),
        description: form.description || null,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
      });
      navigate(soccerPath(`/clubs/${clubId}/admin/tournaments/${created.id}`));
    } catch (err) {
      setError(err.message || 'Failed to create tournament');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (tournament) => {
    if (!window.confirm(`Delete "${tournament.name}"? This cannot be undone.`)) return;
    try {
      await api.deleteClubTournament(clubId, tournament.id, user.id);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to delete tournament');
    }
  };

  return (
    <div className="club-tournament-admin card">
      <div className="club-tournament-admin__header">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate(soccerPath(`/clubs/${clubId}/admin`))}
        >
          ← Back to admin
        </button>
        <h2>Tournaments{club ? ` — ${club.name}` : ''}</h2>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? 'Cancel' : 'New tournament'}
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {showCreateForm && (
        <form className="tournament-create-form" onSubmit={handleCreate}>
          <h3>Create tournament</h3>
          <div className="form-group">
            <label htmlFor="tournament-name">Name *</label>
            <input
              id="tournament-name"
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Summer Cup 2026"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="tournament-desc">Description</label>
            <textarea
              id="tournament-desc"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              placeholder="Annual club tournament..."
            />
          </div>
          <div className="tournament-create-form__dates">
            <div className="form-group">
              <label htmlFor="tournament-start">Start date</label>
              <input
                id="tournament-start"
                type="date"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label htmlFor="tournament-end">End date</label>
              <input
                id="tournament-end"
                type="date"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
              />
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={creating}>
            {creating ? 'Creating…' : 'Create tournament'}
          </button>
        </form>
      )}

      {loading && <p>Loading tournaments…</p>}

      {!loading && tournaments.length === 0 && !showCreateForm && (
        <p className="club-tournament-admin__empty">
          No tournaments yet. Create one to start tracking live scores and stats.
        </p>
      )}

      {!loading && tournaments.length > 0 && (
        <ul className="club-tournament-admin__list">
          {tournaments.map((t) => {
            const badge = statusBadge(t.status);
            return (
              <li key={t.id} className="club-tournament-admin__item">
                <div>
                  <strong>{t.name}</strong>
                  <span className={`tournament-badge ${badge.className}`}>{badge.label}</span>
                  {t.publishedToPublic && (
                    <span className="tournament-badge tournament-badge--published">Public</span>
                  )}
                  <p className="club-tournament-admin__meta">
                    {formatDate(t.startDate)} – {formatDate(t.endDate)}
                    {' · '}
                    {(t.fixtures || []).length} fixture{(t.fixtures || []).length !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="club-tournament-admin__actions">
                  {t.publishedToPublic && t.shareToken && (
                    <a
                      href={soccerUrl(`/public/tournament/${t.shareToken}`)}
                      className="btn btn-secondary"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View public
                    </a>
                  )}
                  <Link
                    to={soccerPath(`/clubs/${clubId}/admin/tournaments/${t.id}`)}
                    className="btn btn-primary"
                  >
                    Manage
                  </Link>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => handleDelete(t)}
                  >
                    Delete
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ClubTournamentAdmin;
